import { useEffect } from "react";
import { useSettings } from "@/context/SettingsContext";
import { PF_ROLE_KEYS } from "@/lib/panchangRoles";
import { TG_ROLES, TG_FONT_DEFAULTS, TG_SCOPE_KEYS, TG_LANG_ROLES, scriptFallbackFor } from "@/lib/typographyRoles";

// Curated Google Fonts. Latin (serif/sans) and Devanagari-capable families.
export const LATIN_SERIF = ["Cormorant Garamond", "EB Garamond", "Playfair Display", "Lora", "Merriweather", "Spectral", "Crimson Pro", "Libre Baskerville", "Bitter", "Source Serif 4"];
export const LATIN_SANS = ["Work Sans", "Inter", "Poppins", "Nunito Sans", "Mulish", "Source Sans 3", "Karla", "Rubik", "DM Sans", "Manrope"];
export const DEVANAGARI = ["Tiro Devanagari Hindi", "Noto Serif Devanagari", "Noto Sans Devanagari", "Mukta", "Hind", "Baloo 2", "Martel", "Rozha One", "Kalam", "Yatra One"];

export const fontOptionsFor = (role) => (role.deva ? DEVANAGARI : [...LATIN_SERIF, ...LATIN_SANS]);

const gfUrl = (families) => {
  if (!families.length) return null;
  const parts = families.map((f) => `family=${f.trim().replace(/\s+/g, "+")}:ital,wght@0,400;0,500;0,600;0,700;1,400`);
  return `https://fonts.googleapis.com/css2?${parts.join("&")}&display=swap`;
};

const devaFallback = (deva) => (deva ? "'Noto Sans Devanagari', sans-serif" : "'Noto Serif Devanagari', serif");

// Build CSS for one settings object, scoped behind `prefix` (a selector that
// every rule is nested under). `useDefaults` = apply built-in fallbacks (Global).
function buildScopeCss(cfg, prefix, useDefaults) {
  let vars = "";
  let rules = "";
  TG_ROLES.forEach((role) => {
    const family = (cfg[role.key] || (useDefaults ? TG_FONT_DEFAULTS[role.key] : "") || "").trim();
    const famDecl = family ? `'${family}', ${devaFallback(role.deva)}` : "";
    if (family) {
      if (role.fontVar) role.fontVar.forEach((v) => { vars += `${v}:${famDecl};`; });
      else rules += `${prefix} ${role.selector}{font-family:${famDecl} !important;}`;
    }
    const sz = parseFloat(cfg[`${role.key}_size`]);
    if (sz && sz > 0) rules += `${prefix} ${role.selector}{font-size:${sz}rem !important;}`;
    const col = (cfg[`${role.key}_color`] || "").trim();
    if (col) rules += `${prefix} ${role.selector}{color:${col} !important;}`;
  });
  // Fine-tuning (global-style knobs) honoured per scope too.
  const weight = (cfg.heading_weight || "").toString().trim();
  const scale = parseFloat(cfg.reading_scale || "1") || 1;
  if (weight) rules += `${prefix} .font-serif-latin,${prefix} .font-deva{font-weight:${weight} !important;}`;
  if (scale && scale !== 1) rules += `${prefix} .reading-content,${prefix} .prose-content{font-size:calc(1.0625rem * ${scale});}`;
  const varBlock = vars ? `${prefix}{${vars}}` : "";
  return varBlock + rules;
}

// Prefix EACH comma-separated part of a selector (so `.a, .b` both get scoped).
const scopeSel = (prefix, selector) => selector.split(",").map((s) => `${prefix} ${s.trim()}`).join(",");

// Build CSS for ONE language's per-role Font/Size/Colour, scoped to
// html[lang="xx"] so it only applies when that language is active — platform-wide,
// but never on the admin console or the Panchang pages (own typography engine).
function buildLangCss(code, cfg) {
  if (!cfg || !Object.keys(cfg).length) return "";
  const prefix = `html[lang="${code}"] body:not([data-tg-scope="admin"]):not([data-tg-scope="panchang"])`;
  const fb = scriptFallbackFor(code);
  let rules = "";
  TG_LANG_ROLES.forEach((role) => {
    const sel = scopeSel(prefix, role.selector);
    const fam = (cfg[role.key] || "").trim();
    if (fam) rules += `${sel}{font-family:'${fam}', ${fb} !important;}`;
    const sz = parseFloat(cfg[`${role.key}_size`]);
    if (sz && sz > 0) rules += `${sel}{font-size:${sz}rem !important;}`;
    const col = (cfg[`${role.key}_color`] || "").trim();
    if (col) rules += `${sel}{color:${col} !important;}`;
  });
  return rules;
}

// Applies the admin-chosen typography at runtime. Everything is scoped behind
// `body[data-tg-scope]` so (a) the admin console (scope="admin") is never touched
// and (b) per-page overrides stay demarcated and don't bleed across the site.
export default function TypographyLoader() {
  const { typography, panchang_fonts, customFonts } = useSettings();

  useEffect(() => {
    const t = typography || {};
    const pages = t.pages || {};
    const langs = t.langs || {};
    const fonts = customFonts || [];
    const root = document.documentElement;

    // 0) Inject @font-face for every admin-uploaded custom font so the chosen
    // families render everywhere (public pages + admin dropdown previews).
    const FACE_ID = "dh-custom-fontfaces";
    let faces = document.getElementById(FACE_ID);
    const faceCss = fonts.map((f) => `@font-face{font-family:'${f.family}';src:url('${f.url}') format('${f.format || "woff2"}');font-display:swap;}`).join("");
    if (!faceCss) { if (faces) faces.remove(); }
    else { if (!faces) { faces = document.createElement("style"); faces.id = FACE_ID; document.head.appendChild(faces); } if (faces.textContent !== faceCss) faces.textContent = faceCss; }
    const customFamilies = new Set(fonts.map((f) => f.family));

    // 1) Panchangam per-role fonts -> CSS variables (applied via .pf-* classes).
    const pfonts = panchang_fonts || {};
    const pfCfg = (v) => (typeof v === "string" ? { font: v } : (v || {}));
    PF_ROLE_KEYS.forEach((key) => {
      const c = pfCfg(pfonts[key]);
      const setOrClear = (suffix, val) => { if (val) root.style.setProperty(`--pf-${key}-${suffix}`, val); else root.style.removeProperty(`--pf-${key}-${suffix}`); };
      setOrClear("font", (c.font || "").trim() ? `'${(c.font).trim()}'` : "");
      setOrClear("size", (c.size || "").toString().trim() ? `${parseFloat(c.size)}px` : "");
      setOrClear("color", (c.color || "").trim());
    });

    // 2) Build scoped CSS. GLOBAL applies to every page EXCEPT the admin console
    // AND the Panchang/calendar pages — Panchang has its own dedicated typography
    // editor (--pf-* vars), so global fonts/sizes must never bleed onto it.
    let css = buildScopeCss(t, 'body:not([data-tg-scope="admin"]):not([data-tg-scope="panchang"])', true);
    TG_SCOPE_KEYS.filter((k) => k !== "global").forEach((k) => {
      const cfg = pages[k];
      if (cfg && Object.keys(cfg).length) css += buildScopeCss(cfg, `body[data-tg-scope="${k}"]`, false);
    });
    // 2b) Per-language overrides (platform-wide, keyed on <html lang>).
    Object.entries(langs).forEach(([code, cfg]) => { css += buildLangCss(code, cfg); });

    const TUNE_ID = "dh-typography-tune";
    let tune = document.getElementById(TUNE_ID);
    if (!css) { if (tune) tune.remove(); }
    else { if (!tune) { tune = document.createElement("style"); tune.id = TUNE_ID; document.head.appendChild(tune); } tune.textContent = css; }

    // 3) Load any chosen families (global + page scopes + languages + panchang)
    // from Google Fonts — except the preloaded ones and admin-uploaded custom fonts.
    const preloaded = new Set(["Google Sans", "Cormorant Garamond", "Tiro Devanagari Hindi", "Noto Serif Devanagari", "Noto Sans Devanagari", "Work Sans"]);
    const fromCfg = (cfg) => TG_ROLES.map((r) => (cfg[r.key] || "").trim());
    const fromLangCfg = (cfg) => TG_LANG_ROLES.map((r) => (cfg[r.key] || "").trim());
    const toLoad = [...new Set([
      ...fromCfg(t),
      ...Object.values(pages).flatMap((cfg) => fromCfg(cfg || {})),
      ...Object.values(langs).flatMap((cfg) => fromLangCfg(cfg || {})),
      ...Object.values(panchang_fonts || {}).map((v) => (typeof v === "string" ? v : (v && v.font) || "").trim()),
    ].filter((f) => f && !preloaded.has(f) && !customFamilies.has(f)))];
    const href = gfUrl(toLoad);
    const ID = "dh-typography-fonts";
    const existing = document.getElementById(ID);
    if (!href) { if (existing) existing.remove(); }
    else if (!existing || existing.getAttribute("href") !== href) {
      if (existing) existing.remove();
      const link = document.createElement("link");
      link.id = ID; link.rel = "stylesheet"; link.href = href;
      document.head.appendChild(link);
    }
  }, [typography, panchang_fonts, customFonts]);

  return null;
}
